import { Check, ChevronDown } from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { useMyVoicePresenceStatus, useSetMyVoicePresence } from '../../hooks/use-voice-presence';
import { VOICE_PRESENCE_STATUSES, type VoicePresenceStatus } from '../../types';

const STATUS_META: Record<VoicePresenceStatus, { label: string; dotClass: string }> = {
    ACTIVE: { label: 'Active', dotClass: 'bg-success-500' },
    AWAY: { label: 'Away', dotClass: 'bg-warning-500' },
    BUSY: { label: 'Busy', dotClass: 'bg-error-500' },
    OFFLINE: { label: 'Offline', dotClass: 'bg-neutral-400' },
};

/**
 * Agent-availability dropdown in the widget header — compact dot + chevron
 * trigger (the header row is tight); the menu items carry the labels. Only
 * Active agents with a live voice connection are dialed for inbound calls.
 */
export function PresenceStatusMenu() {
    const status = useMyVoicePresenceStatus();
    const setPresence = useSetMyVoicePresence();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                disabled={setPresence.isPending}
                aria-label="Change availability status"
                title={`Status: ${STATUS_META[status].label}`}
                className="flex shrink-0 items-center gap-1 rounded-full border border-white/60 bg-white/60 px-2 py-1 transition-colors hover:bg-white/80 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.06] dark:hover:bg-white/10"
            >
                <span className={cn('size-2 rounded-full', STATUS_META[status].dotClass)} />
                <ChevronDown className="size-3 text-text-tertiary" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-1001 w-36 min-w-0">
                {VOICE_PRESENCE_STATUSES.map((candidate) => (
                    <DropdownMenuItem
                        key={candidate}
                        onClick={() => {
                            if (candidate !== status) setPresence.mutate(candidate);
                        }}
                        className="gap-2 text-xs"
                    >
                        <span
                            className={cn('size-2 rounded-full', STATUS_META[candidate].dotClass)}
                        />
                        {STATUS_META[candidate].label}
                        {candidate === status && (
                            <Check className="ml-auto size-3.5 text-text-tertiary" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
